import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChantingPlayer from "@/components/ChantingPlayer";
import { useSEO } from "@/lib/useSEO";
import { Flame, MapPin, CalendarDays, Heart, Radio } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const MILESTONES = [
  {
    date: "April 29, 2023",
    title: "The Pilgrimage to Kainchi Dham",
    text: "A journey to Neem Karoli Baba's ashram in the Kumaon hills near Nainital — where the prayer 'Baba, come home to Texas with us' took root.",
    img: "/images/kainchi-valley.webp",
    testid: "milestone-kainchi",
  },
  {
    date: "November 3, 2023",
    title: "Hanuman-ji Arrives in Denison",
    text: "The black granite murti of Lord Hanuman reached 1642 FM 1310 by flatbed and crane, and was set upon Texas earth under a wide blue sky.",
    img: "/images/murti-arrival-1.jpg",
    testid: "milestone-arrival",
  },
  {
    date: "November 4, 2023",
    title: "The First Hanuman Chalisa",
    text: "No roof, no walls — only grass, sky, and Hanuman-ji. The first Chalisa rose over the open field, and the first head bowed at His feet.",
    img: "/images/first-chalisa-bow.jpg",
    testid: "milestone-chalisa",
  },
  {
    date: "December 4, 2023",
    title: "The Mandir's Inauguration",
    text: "Baba's hand-carved wooden altar was adorned with fresh marigolds — Maharaj-ji's smiling presence formally welcomed home.",
    img: "/images/mandir-inauguration.jpg",
    testid: "milestone-inauguration",
  },
  {
    date: "Every Saturday, Today",
    title: "Doors Open in Seva",
    text: "Morning darshan 8:30–11:30 AM, evening aarti 5:00–8:30 PM, and bhandara for all — lovingly tended by a devoted family.",
    img: "/images/devotee-gathering.webp",
    testid: "milestone-today",
  },
  {
    date: "The Road Ahead",
    title: "A Bigger Temple for Baba",
    text: "A $2 million dream taking shape — a proper temple, meditation hall, devotee accommodation, gardens, and a kitchen & dining hall.",
    img: "/images/hanuman-sunset-1.webp",
    testid: "milestone-ahead",
  },
];

const FAMILY = [
  { name: "Veena Thakkar", role: "Instrument of Grace", note: "She became instrumental in bringing this divine initiative to life — for all devotees of Baba. Now it is yours and ours, to enjoy and to pass along.", testid: "family-card-veena", featured: true },
  { icon: Radio, role: "Community Voice", note: "Carries Baba's message to the whole community through FunAsia's media family.", testid: "family-card-voice" },
];

export default function AboutUsPage() {
  useSEO({
    title: "Our Story | Neem Karoli Baba Temple Dallas–DFW — Osho Kaivalya Dham",
    description: "How Neem Karoli Baba (Maharajji), revered as a Hanuman Ansh — an avatar of Lord Hanuman — came home to Denison, Texas. The founding story of the DFW Baba temple.",
    canonical: "https://neemkarolibabadallas.org/about-us",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://neemkarolibabadallas.org/" },
        { "@type": "ListItem", position: 2, name: "About Us", item: "https://neemkarolibabadallas.org/about-us" },
      ],
    },
  });
  return (
    <div data-testid="about-us-page" className="relative">
      <div className="grain-overlay" aria-hidden="true" />
      <Navbar basePath="/" />
      <main>
        <section className="bg-[#1C1917] pt-36 sm:pt-44 pb-16 sm:pb-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{ backgroundImage: "radial-gradient(circle at 70% 20%, #F59E0B 0%, transparent 45%)" }} />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
              className="eyebrow !text-amber-400 mb-4">About Us</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-amber-50" data-testid="about-us-heading">
              How Baba Decided to Come to Texas
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
              className="mt-5 text-stone-400 max-w-2xl leading-relaxed">
              A prayer answered in granite and marigolds — and a plan written by the Divine
              long before any of us arrived.
            </motion.p>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-[#FDFBF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}>
                <p className="eyebrow mb-4">The Calling</p>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-amber-950" data-testid="about-us-calling-heading">
                  "Baba — Come Home to Texas With Us"
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stone-700">
                  In April 2023, our founder Sw. Prem Maitreya (Poorvesh Thakkar) — just a seeker on the path — stood at the gates of Kainchi Dham —
                  Maharaj-ji's own ashram in the Himalayas — and felt what so many before him have felt:
                  that Baba's love does not belong to one valley, one country, or one people.
                </p>
                <p className="mt-4 text-base leading-relaxed text-stone-700">
                  Within seven months, a black granite Hanuman-ji stood on Texas soil. Within eight, Baba's
                  mandir was inaugurated. What began as one family's devotion is now a growing satsang that
                  opens its doors — and its kitchen — to everyone, every Saturday.
                </p>
                <p className="mt-4 text-base leading-relaxed text-stone-700">
                  A board of trustees is now being formed to carry this seva forward for generations.
                </p>
              </motion.div>
              <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
                className="diya-glow rounded-2xl overflow-hidden border border-amber-900/15 shadow-2xl shadow-amber-900/10">
                <img src="/images/founder-kainchi-overlook.jpg" alt="Poorvesh Thakkar above Kainchi Dham, Nainital — the ashram's red rooftops in the valley behind him"
                  data-testid="about-us-calling-image" className="relative z-10 w-full aspect-[4/3] object-cover" />
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-[#F5F0E6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="eyebrow mb-4">The Journey</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-amber-950" data-testid="about-us-timeline-heading">
              Milestones of Grace
            </h2>
            <div className="mt-12 space-y-6">
              {MILESTONES.map((m, i) => (
                <motion.div key={m.title}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: 0.05 * (i % 3), ease: EASE }}
                  data-testid={m.testid}
                  className="group grid sm:grid-cols-[180px_1fr_140px] gap-5 items-center bg-white rounded-2xl border border-amber-900/10 p-5 sm:p-6 hover:border-amber-600/40 hover:shadow-lg hover:shadow-amber-900/5 transition-all duration-300"
                >
                  <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                    <CalendarDays className="h-4 w-4 text-amber-600 shrink-0" /> {m.date}
                  </p>
                  <div>
                    <h3 className="font-display text-lg text-amber-950">{m.title}</h3>
                    <p className="text-sm text-stone-600 mt-1 leading-relaxed">{m.text}</p>
                  </div>
                  <div className="hidden sm:flex h-24 w-[140px] rounded-xl overflow-hidden border border-amber-900/10 bg-[#F5F0E6] items-center justify-center">
                    <img src={m.img} alt={m.title} loading="lazy" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="full-story" className="py-16 sm:py-24 bg-[#FDFBF7] relative overflow-hidden" data-testid="full-story-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }} className="text-center">
              <p className="eyebrow mb-4">The Whole Story, Told Slowly</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-amber-950" data-testid="full-story-heading">
                How Baba Came Home to Texas
              </h2>
              <div className="jali-divider my-8 w-56 mx-auto" />
            </motion.div>

            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 1 }} className="space-y-12 text-stone-700">

              <p className="text-center text-xs uppercase tracking-[0.25em] font-semibold text-amber-700" data-testid="full-story-byline">
                As told by our founder, Sw. Prem Maitreya (Poorvesh Thakkar) — just a seeker on the path
              </p>

              <div>
                <h3 className="font-display text-xl text-amber-950 mb-4" data-testid="full-story-part-1">I · A Temple That Began with a Wish</h3>
                <p className="text-base leading-loose drop-cap">
                  Some people make a plan to build a temple. My experience was different. A desire arose quietly
                  in my heart. I did not know how to fulfill it. Yet, little by little, the murti, the people, the
                  transportation, and the temple itself came together. Looking back, I feel that I received the
                  credit for something that happened through Baba's grace. This is how it began.
                </p>
              </div>

              <div>
                <h3 className="font-display text-xl text-amber-950 mb-4" data-testid="full-story-part-2">II · An Unexpected Connection</h3>
                <p className="text-base leading-loose">
                  Around 2023, I was going through a difficult period. I was experiencing physical discomfort,
                  emotional challenges in relationships, and disturbances that I understood as being connected to my
                  energy system. During a visit to a friend's home in India, I noticed a photograph of Hanuman and
                  asked who in her family followed him. "My father," she told me. "He is a Hanuman bhakt. He
                  encouraged us to recite the Hanuman Chalisa from childhood, but we never really took it up." It
                  was a brief conversation, but something about that photograph stayed with me.
                </p>
                <p className="text-base leading-loose mt-4">
                  After returning to America, I was meditating at our ashram, Osho Kaivalya Dham in Denison, when
                  I saw a very large image of Hanuman in meditation. This was unusual for me. My meditation
                  generally brings silence: thoughts disappear, the mind becomes quiet, and I begin to feel the
                  energy within. Images have appeared only on a few occasions. I did not know what to make of it.
                  I simply let the experience be.
                </p>
              </div>

              <div>
                <h3 className="font-display text-xl text-amber-950 mb-4" data-testid="full-story-part-3">III · Fifty Days with the Hanuman Chalisa</h3>
                <p className="text-base leading-loose">
                  A few months later, I went through another difficult period with my health. I felt I needed help,
                  but my business and personal responsibilities made traveling difficult. Meanwhile, the image of
                  Hanuman kept returning to my mind. I also began coming across videos of Neem Karoli Baba. One day,
                  I thought of the Hanuman Chalisa and its promise of strength and relief from suffering. I decided
                  to recite it one hundred times. But I did not want to rush through a hundred recitations as a task
                  to complete. I wanted to spend time with the prayer — to feel its meaning and carry that presence
                  into my day. So I recited it once in the morning and once in the evening for fifty days.
                </p>
                <p className="text-base leading-loose mt-4">
                  During that period, I began to feel better. This surprised me. I had always considered myself a
                  logical person. I did not think of myself as naturally devotional, and I had not been a dedicated
                  devotee of Hanuman or Bhagwan Ram. Yet something in my heart was opening. Around the same time, a
                  teacher whose healing sessions I sought came to Dallas, allowing me to receive support without
                  traveling. My meditation was also deepening. I experienced these things together as a period of
                  recovery, support, and growing trust.
                </p>
              </div>

              <div>
                <h3 className="font-display text-xl text-amber-950 mb-4" data-testid="full-story-part-4">IV · A Friendship and a Journey</h3>
                <p className="text-base leading-loose">
                  During this time, I became close to a friend named Ram. He was a devoted Hanuman bhakt with a deep
                  interest in meditation and spiritual masters. From our first meeting, we connected through
                  meaningful conversations. We eventually began hosting a morning radio show together, speaking
                  about Hanuman, Neem Karoli Baba, Osho, and other saints. Through those conversations, a desire
                  arose in me: I wanted to visit Neem Karoli Baba's Kainchi Dham. The Hanuman Chalisa had become
                  such a meaningful part of my life. I wanted to sit in the place associated with Baba and
                  experience it for myself.
                </p>
                <p className="text-base leading-loose mt-4">
                  The journey was spontaneous. I booked tickets, took my daughter with me, and flew to Delhi. Two
                  friends joined us, and we drove toward Nainital together, without carefully arranged plans or
                  hotel reservations. Eventually, we found a place to stay. The next morning, we went to Kainchi Dham.
                </p>
              </div>

              <div>
                <h3 className="font-display text-xl text-amber-950 mb-4" data-testid="full-story-part-5">V · The Wish at Kainchi Dham</h3>
                <p className="text-base leading-loose">
                  I entered wearing my maroon Osho clothes and my Osho mala. My daughter and I sat quietly for about
                  thirty minutes near Baba's bed and blanket — the familiar setting I had seen in photographs. There
                  was a beautiful simplicity to the place. An aarti was taking place nearby. Osho's teachings and
                  meditations had opened my heart to many spiritual traditions. My connection with Osho had helped
                  me appreciate other masters and their different expressions of truth, silence, love, and devotion.
                </p>
                <p className="text-base leading-loose mt-4">
                  As I was leaving Baba's room, a thought arose: I should take Baba's photograph back to our ashram.
                  Then another thought followed: Perhaps someday we could build a temple for him. I began thinking
                  about a murti — a sacred statue — for the temple. But almost immediately, my practical mind
                  stepped in. Where would I find an artist? How would I arrange the work? What would it cost? How
                  would I transport it to America? My return flight was approaching. There was no time to explore
                  any of it properly. I took photographs and returned home with that wish still in my heart, unsure
                  how it could ever become a reality.
                </p>
              </div>

              <div>
                <h3 className="font-display text-xl text-amber-950 mb-4" data-testid="full-story-part-6">VI · The Murti I Thought Was Buddha</h3>
                <p className="text-base leading-loose">
                  Meanwhile, a doctor friend had been asking me to collect a murti he was holding for me. Months
                  earlier, we had discussed a Buddha murti. The Buddha statue at our ashram had some damage, so when
                  he called again, I assumed that was what he meant. "Yes, yes," I told him. "I will send someone to
                  pick it up." After I returned from India, he called again. The murti was in Odessa, Texas. I
                  arranged for someone local to go and see about collecting it. Then the driver called me. "This is
                  not possible," he said. "The murti is very big." "How big can it be?" I asked. "You have a truck!"
                  He sent me photographs and dimensions. It was a fourteen-foot-tall, six-ton stone murti. And it
                  was not Buddha. It was Hanuman.
                </p>
                <blockquote className="my-8 pl-6 border-l-2 border-amber-500">
                  <p className="font-quote italic text-2xl text-amber-900 leading-snug" data-testid="full-story-quote-1">
                    "मैंने तो अभी बाबा के बारे में सोचा, और उन्होंने हनुमानजी की मूर्ति की व्यवस्था भी कर दी।"
                  </p>
                  <p className="mt-3 text-sm text-stone-500 leading-relaxed">
                    I had only just thought of Baba — and it felt as though he had already arranged Hanumanji's murti.
                  </p>
                </blockquote>
                <p className="text-base leading-loose">
                  I was astonished. I had just returned from Kainchi Dham wondering how I could arrange a murti for
                  a temple. Now an enormous Hanuman murti was available — and it was already in America.
                </p>
              </div>

              <div>
                <h3 className="font-display text-xl text-amber-950 mb-4" data-testid="full-story-part-7">VII · Bringing Hanuman to Denison</h3>
                <p className="text-base leading-loose">
                  In my excitement, I announced that we would open the Neem Karoli Baba temple and unveil the
                  Hanuman murti on December 11. Then came the practical challenge. Nobody wanted to transport it.
                  The murti was extremely heavy, and moving such a large stone sculpture carried risks. We contacted
                  transport companies and asked friends for help. Even a friend experienced in international
                  shipping could not find someone willing to take the job. Weeks passed. The opening date was
                  approaching, and the murti was still in Odessa. I began searching and making calls myself.
                  Eventually, one man agreed to inspect it. "I can bring it," he told me, "but I cannot insure it
                  against breaking." I decided to proceed. We arranged a large crane in Odessa to lift the murti
                  onto the truck, and another crane in Denison to unload it and place it where it stands today.
                  After all the uncertainty, Hanuman had arrived. But we still did not have a finished temple.
                </p>
              </div>

              <div>
                <h3 className="font-display text-xl text-amber-950 mb-4" data-testid="full-story-part-8">VIII · My Mother Steps In</h3>
                <p className="text-base leading-loose">
                  With about ten days remaining before the announced opening, my mother came to my office with my
                  aunt. "You have announced the opening," she said. "How are you going to have the temple ready?"
                  She had found a ready-made building that we could use. She took me to see it, and we bought it.
                  The interior was unfinished. There was still work to do, including finishing the walls and
                  painting. My mother also took me to purchase a mandir for inside the building. She funded those
                  arrangements and hired a handyman to prepare the space. My part was to help manage the work —
                  including a difficult moment near the opening when the handyman wanted to leave before finishing.
                  Even then, things kept coming together. The day before the inauguration, the walls were still
                  rough and painting remained unfinished. It was not a perfectly completed building. But we opened
                  the temple.
                </p>
              </div>

              <div>
                <h3 className="font-display text-xl text-amber-950 mb-4" data-testid="full-story-part-9">IX · A Temple for Everyone</h3>
                <p className="text-base leading-loose">
                  During our first aarti, I felt Neem Karoli Baba's presence beside the murti. That remains a deeply
                  personal experience for me. Even now, I feel his love and grace here. Visitors come from across
                  the United States, and many share their own feelings of peace, connection, and devotion on this
                  land. When people say that I built Baba's temple, I remember how little of it I had planned. The
                  desire arose at Kainchi Dham. The Hanuman murti was waiting in Texas. Someone finally agreed to
                  bring it. My mother and aunt stepped in when the temple itself was still unfinished. I made phone
                  calls, arranged things, and helped where I could. But I experienced the unfolding of it all as
                  grace.
                </p>
                <blockquote className="my-8 pl-6 border-l-2 border-amber-500">
                  <p className="font-quote italic text-2xl text-amber-900 leading-snug" data-testid="full-story-quote-2">
                    "This is Baba's temple. This is Hanuman's temple. It is here for all of you."
                  </p>
                </blockquote>
                <p className="text-base leading-loose">
                  At Osho Kaivalya Dham in Denison, we also offer a space to explore Osho's meditations —
                  practices that invite awareness, emotional expression, inner silence, and a more loving
                  relationship with life. For me, meditation and devotion have found a home together here. You are
                  welcome to visit, sit quietly, join in prayer, or meditate. Come with an open heart. The
                  invitation is to experience this place for yourself — and, perhaps, to discover a little more
                  love, silence, and grace within you.
                </p>
                <p className="text-base leading-loose mt-6 font-semibold text-amber-900">
                  Jai Shri Ram. Jai Hanuman. Jai Maharaj-ji.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-[#F5F0E6]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="eyebrow mb-4">The Hands That Serve</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-amber-950" data-testid="about-us-family-heading">
              The Divine Behind Every Temple
            </h2>
            <p className="mt-4 text-stone-600 max-w-2xl leading-relaxed">
              Behind this temple — as behind every temple in the world — are the hands of the Divine.
              We simply became instruments of existence for its plan. The mandir is lovingly looked after
              by a devoted family, together with a growing circle of devotees — and everyone who walks
              in becomes family too.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
              {FAMILY.map((f, i) => (
                <motion.div key={f.testid}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
                  data-testid={f.testid}
                  className={`rounded-2xl border p-7 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${
                    f.featured
                      ? "bg-gradient-to-b from-amber-50 to-white border-amber-500/40 shadow-md shadow-amber-900/10 hover:shadow-amber-900/20"
                      : "bg-white border-amber-900/10 hover:border-amber-600/40 hover:shadow-amber-900/5"
                  }`}
                >
                  <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-b from-amber-100 to-orange-100 border border-amber-900/10 font-display text-xl font-semibold text-orange-800">
                    {f.name ? f.name.split(" ").map((w) => w[0]).join("") : <f.icon className="h-6 w-6" />}
                  </span>
                  <h3 className="font-display text-lg text-amber-950">{f.name || f.role}</h3>
                  {f.name && <p className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold mt-1">{f.role}</p>}
                  <p className="text-sm text-stone-500 mt-3 leading-relaxed">{f.note}</p>
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
              viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="mt-8 text-center text-sm text-stone-500"
              data-testid="family-special-credits"
            >
              Supported by a devoted family — and cherished by devotees of Baba across DFW.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.9, ease: EASE }}
              className="mt-16 rounded-3xl bg-[#1C1917] px-8 py-14 sm:px-16 text-center relative overflow-hidden"
              data-testid="about-us-vision"
            >
              <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{ backgroundImage: "radial-gradient(circle at 50% 0%, #F59E0B 0%, transparent 55%)" }} />
              <p className="relative flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold mb-8">
                <Heart className="h-4 w-4" /> The Dream
              </p>
              <blockquote className="relative font-quote italic text-2xl sm:text-3xl text-amber-50 leading-snug max-w-3xl mx-auto">
                "A place to come sit in silence and feel the presence of Baba, Hanuman and Ram —
                a place to drown in devotion and love for all."
              </blockquote>
              <p className="relative mt-5 text-xs uppercase tracking-[0.25em] text-amber-300/80">— Sw. Prem Maitreya (Poorvesh Thakkar) · Just a Seeker on the Path, Sharing His Love for Baba and You</p>
              <div className="relative mt-9 flex flex-wrap justify-center gap-4">
                <a href="/#visit" data-testid="about-us-visit-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-amber-200/40 bg-amber-50/10 px-7 py-3 text-sm font-semibold text-amber-50 hover:bg-amber-50/20 transition-all duration-300">
                  <MapPin className="h-4 w-4" /> Visit Us This Saturday
                </a>
                <a href="/#donate" data-testid="about-us-donate-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-7 py-3 text-sm font-semibold text-white shadow-xl shadow-orange-950/40 hover:-translate-y-0.5 transition-all duration-300">
                  <Flame className="flame h-4 w-4" /> Help Build the New Temple
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <ChantingPlayer />
    </div>
  );
}
